import React, { useEffect, useState, useContext } from "react";
import { makeStyles } from "@mui/styles";

//... Shared components.
import Loader from "../../sharedComponents/loader/loader";
import AlertSnackbar from "../../sharedComponents/Snackbar/AlertSnackbar";

//... Context provider
import { AppDataContext } from "../../common/appContext";

//... Services imports
import { TeamPlayerServices } from "../../services/teamPlayers.Service";

//... Child imports
import ManagePlayersAssignments from "./ManagePlayersAssignments";
import ManageTeamDialog from "./ManageTeamDialog";
import AddGameDialog from "./AddGameDialog";
import EventGameToolbar from "./EventGameToolbar";
import { useNavigate } from "react-router-dom";
import RouteConstants from "../../common/RouteConstants";

const useStyles = makeStyles((theme) => ({
  fileInput: {
    display: "flex",
    flex: "1",
    alignItems: "center",
  },
  headerBox: {
    background: "#c1d0ed",
  },
  headerText: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  tableRow: {
    "&:nth-child(odd)": {
      background: "#f5f5f5",
    },
    "& .MuiTableCell-root": {
      borderBottom: "none",
    },
  },
  iconStyle: {
    width: "26px",
    height: "26px",
    cursor: "pointer",
  },
  appbarBackground: {
    backgroundColor: "#fff",
    color: "#1d428a",
  },
  title: {
    flexGrow: 1,
  },
  gridItem: {
    paddingTop: "10px",
  },
}));

const ManageTeamPlayersContainer = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const { user, selectedEventId, selectedEvent, combineYear } =
    useContext(AppDataContext);

  //... State holders
  const [loading, setLoading] = useState(false);
  const [teamList, setTeamList] = useState([]);
  const [playersList, setPlayersList] = useState([]);
  const [storedTeamList, setStoredTeamList] = useState([]);
  const [eventGameList, setEventGameList] = useState({
    optionList: [],
    gridList: [],
  });
  const [seletedGameVal, setSeletedGameVal] = useState(0);
  const [allFiltersSelected, setAllFiltersSelected] = useState(false);

  //.... Dialog State Holders
  const [eventGameTeamDetails, setEventGameTeamDetails] = useState({
    eventTeamId: 0,
    teamName: "",
  });
  const [eventGameTeamEditMode, setEventGameTeamEditMode] = useState(false);
  const [eventGameDetails, setEventGameDetails] = useState({
    eventGameId: 0,
    gameName: "",
  });
  const [eventGameEditMode, setEventGameEditMode] = useState(false);
  const [eventGameDialogOpen, setEventGameDialogOpen] = useState(false);
  const [addGameDialogOpen, setAddGameDialogOpen] = useState(false);

  //... Alert State holders.
  const [alert, setAlert] = useState({
    open: false,
    type: "warning",
    message: "",
  });

  useEffect(() => {
    if (selectedEventId) {
      resetDependecies();
      getEventGamesByEventID(selectedEventId);
    }
  }, [selectedEventId]);

  useEffect(() => {
    seletedGameVal ? fetchData(seletedGameVal) : resetDependecies();
    setAllFiltersSelected(seletedGameVal !== 0);
  }, [seletedGameVal]);

  const fetchData = async (seletedGameVal) => {
    if (seletedGameVal > 0) {
      const tempPlayerList = await fetchEventGamePlayerList(seletedGameVal);
      const tempTeamList = await fetchEventGameTeamList(seletedGameVal);

      setTeamList([
        { eventTeamId: 0, teamName: "Players List" },
        ...tempTeamList,
      ]);
      setStoredTeamList(tempTeamList || []);
      setPlayersList(tempPlayerList || []);
    }
  };

  //... Manage Game Teams for Event Handlers
  const handleSaveEventGameTeamData = async (data) => {
    if (!data?.teamName?.trim()) {
      showAlert("warning", "The game team name field cannot be empty...");
      return;
    }

    const action = data.eventTeamId > 0 ? "update" : "add";
    await saveOrUpdateEventGameTeamDetails(data, action);
  };

  const handleEventGameTeamEdit = ({ eventTeamId, teamName }) => {
    setEventGameTeamEditMode(true);
    setEventGameTeamDetails({
      eventTeamId: eventTeamId,
      teamName: teamName,
    });
  };

  const handleEventGameTeamDelete = async (data) => {
    await deleteEventGameTeamDetails(data);
  };

  //... Manage Games for Event Handlers
  const handleSaveEventGameDetails = async (data) => {
    if (!data?.gameName?.trim()) {
      showAlert("warning", "The game name field cannot be empty...");
      return;
    }

    await saveOrUpdateEventGameDetails(
      data,
      data.eventGameId > 0 ? "update" : "add"
    );
  };

  const handleEventGameEdit = ({ eventGameId, gameName }) => {
    setEventGameEditMode(true);
    setEventGameDetails({
      eventGameId: eventGameId,
      gameName: gameName,
    });
  };

  const handleEventGameDelete = async (data) => {
    const isSelectedGame = data?.eventGameId === seletedGameVal;
    await deleteEventGameDetails(data, isSelectedGame);
  };

  //...Common Handlers
  const handleEventGameCloseDialog = () => {
    setEventGameDialogOpen(false);
  };

  const handleEventGameOpenDialog = () => {
    setEventGameDialogOpen(true);
  };

  const handleCloseAddGameDialog = () => {
    setAddGameDialogOpen(false);
  };

  const handleOpenAddGameDialog = () => {
    setAddGameDialogOpen(true);
  };

  const handleViewTeamRosters = () => {
    navigate(RouteConstants.VIEW_TEAM_ROSTER);
  };

  const handleManageGameTeams = async () => {
    // const tempTeamList = await fetchEventGameTeamList(seletedGameVal);
    // setStoredTeamList(tempTeamList || []);
    resetEventGameTeamFields();
    handleEventGameOpenDialog();
  };

  const showAlert = (type, message) => {
    setAlert({ open: true, type, message });
  };

  const updateEventGameTeamPlayer = async () => {
    const updatedList = playersList
      .filter((item) => item.updatedTeamId > 0)
      .map((item) => ({ teamId: item.updatedTeamId, playerId: item.playerId }));

    await updateEventGameTeamPlayerList(updatedList);
  };

  const resetEventGameTeamFields = () => {
    setEventGameTeamEditMode(false);
    setEventGameTeamDetails({
      eventGameId: 0,
      gameName: "",
    });
  };

  const resetEventGameFields = () => {
    setEventGameEditMode(false);
    setEventGameDetails({
      eventGameId: 0,
      gameName: "",
    });
  };

  const resetDependecies = () => {
    setTeamList([]);
    setPlayersList([]);
    setAllFiltersSelected(false);
    setSeletedGameVal(0);
  };

  //... Apis
  async function getEventGamesByEventID(selectedEventId) {
    try {
      setLoading(true);
      const emptyGameList = { eventGameId: 0, gameName: "--Select Game--" };
      const { data } = await TeamPlayerServices.getEventGames(
        parseInt(selectedEventId)
      );

      setEventGameList({
        optionList: [emptyGameList, ...(data || [])],
        gridList: data || [],
      });
    } catch (error) {
      console.error(`Error fetching :`, error);
    } finally {
      setLoading(false);
    }
  }

  const saveOrUpdateEventGameDetails = async (formObj, action) => {
    const actionMap = {
      add: { method: "addEventGame", message: "Added" },
      update: { method: "updateEventGame", message: "Updated" },
    };

    try {
      const { method, message } = actionMap[action];

      const { data: { data: resultData, error } = {} } =
        await TeamPlayerServices[method]({
          ...formObj,
          eventId: parseInt(selectedEventId),
          processedBy: user.loginID,
        });

      showAlert(
        resultData ? "success" : "warning",
        resultData
          ? `Event Game details have been ${message} successfully...`
          : error ||
              `An error occurred while ${action}ing the Event Game details...`
      );

      if (resultData) {
        await getEventGamesByEventID(parseInt(selectedEventId));
        resetEventGameFields();
        handleCloseAddGameDialog();
      }
    } catch (err) {
      console.error(err);
      showAlert(
        "error",
        `An error occurred while ${action}ing the Event Game details...`
      );
    }
  };

  const deleteEventGameDetails = async (formObj, isSelectedGame = false) => {
    try {
      const { data: { data: resultData, error } = {} } =
        await TeamPlayerServices.deleteEventGame({
          ...formObj,
          eventId: parseInt(selectedEventId),
          processedBy: user.loginID,
        });

      showAlert(
        resultData ? "success" : "warning",
        resultData
          ? "Event Game details have been deleted successfully..."
          : error || "An error occurred while deleting the Event Game details."
      );

      if (resultData) {
        await getEventGamesByEventID(parseInt(selectedEventId));
        isSelectedGame && resetDependecies();
        resetEventGameFields();
        handleCloseAddGameDialog();
      }
    } catch (err) {
      console.error(err);
      showAlert(
        "error",
        "An error occurred while deleting the Event Game details..."
      );
    }
  };

  const fetchEventGamePlayerList = async (seletedGameVal) => {
    try {
      const { data } = await TeamPlayerServices.getEventGameTeamPlayers(
        parseInt(seletedGameVal)
      );

      return data;
    } catch (error) {
      console.error(`Error fetching :`, error);
    }
  };

  const fetchEventGameTeamList = async (selectedGameVal) => {
    try {
      setLoading(true);
      const { data = [] } = await TeamPlayerServices.getEventGameTeams(
        parseInt(selectedGameVal)
      );
      return data;
    } catch (e) {
      console.error("Error - fetchEventGameTeamList:", e);
    } finally {
      setLoading(false);
    }
  };

  const saveOrUpdateEventGameTeamDetails = async (formObj, action) => {
    const actionMap = {
      add: { method: "addEventGameTeam", message: "saved" },
      update: { method: "updateEventGameTeam", message: "updated" },
    };

    try {
      const { method, message } = actionMap[action];

      // Call the appropriate service method based on the action
      const { data: { data: resultData, error } = {} } =
        await TeamPlayerServices[method]({
          ...formObj,
          eventGameId: parseInt(seletedGameVal),
          processedBy: user.loginID,
        });

      showAlert(
        resultData ? "success" : "warning",
        resultData
          ? `Event Game Team details have been ${message} successfully...`
          : error ||
              `An error occurred while ${action}ing the Event Game Team details...`
      );

      if (resultData) {
        await fetchData(seletedGameVal);
        resetEventGameTeamFields();
        handleEventGameCloseDialog();
      }
    } catch (err) {
      console.log(err);
      showAlert(
        "error",
        `An error occurred while ${action}ing the Event Game Team details...`
      );
    }
  };

  const deleteEventGameTeamDetails = async (formObj) => {
    try {
      const { data: { data: resultData, error } = {} } =
        await TeamPlayerServices.deleteEventGameTeam({
          ...formObj,
          eventGameId: parseInt(seletedGameVal),
          processedBy: user.loginID,
        });

      showAlert(
        resultData ? "success" : "warning",
        resultData
          ? "Event Game Team details have been deleted successfully..."
          : error ||
              "One or more players have been assigned to this team. Please delete players first and then delete the team."
      );

      if (resultData) {
        await fetchData(seletedGameVal);
        resetEventGameTeamFields();
        handleEventGameCloseDialog();
      }
    } catch (err) {
      console.error(err);
      showAlert(
        "error",
        "An error occurred while deleting the Event Game Team details..."
      );
    }
  };

  const updateEventGameTeamPlayerList = async (list) => {
    try {
      await TeamPlayerServices.updateEventGameTeamPlayers({
        eventGameId: parseInt(seletedGameVal),
        teamPlayers: list,
        processedBy: user.loginID,
      });

      showAlert(
        "success",
        "Event Game Players have been successfully updated for teams...."
      );
      await fetchData(seletedGameVal);
    } catch (err) {
      console.log(err);
      showAlert(
        "error",
        "An error occurred while updating players for the event game teams..."
      );
    }
  };

  return (
    <>
      <Loader isLoading={loading} />
      <EventGameToolbar
        eventGameList={eventGameList}
        seletedGameVal={seletedGameVal}
        setSeletedGameVal={setSeletedGameVal}
        handleManageGameTeams={handleManageGameTeams}
        handleOpenAddGameDialog={handleOpenAddGameDialog}
        allFiltersSelected={allFiltersSelected}
        handleViewTeamRosters={handleViewTeamRosters}
        classes={classes}
      />

      <ManagePlayersAssignments
        teamList={teamList}
        playersList={playersList}
        setPlayersList={setPlayersList}
        updateEventGameTeamPlayer={updateEventGameTeamPlayer}
      />

      <ManageTeamDialog
        DialogOpen={eventGameDialogOpen}
        handleEventGameCloseDialog={handleEventGameCloseDialog}
        teamDetails={storedTeamList || []}
        eventGameTeamDetails={eventGameTeamDetails}
        eventGameTeamEditMode={eventGameTeamEditMode}
        setEventGameTeamDetails={setEventGameTeamDetails}
        handleSaveEventGameTeamData={handleSaveEventGameTeamData}
        handleEventGameTeamEdit={handleEventGameTeamEdit}
        handleEventGameTeamDelete={handleEventGameTeamDelete}
      />

      <AddGameDialog
        addGameDialogOpen={addGameDialogOpen}
        handleCloseAddGameDialog={handleCloseAddGameDialog}
        eventGameDetails={eventGameDetails}
        eventGameEditMode={eventGameEditMode}
        setEventGameDetails={setEventGameDetails}
        eventGameList={eventGameList?.gridList || []}
        handleSaveEventGameDetails={handleSaveEventGameDetails}
        handleEventGameEdit={handleEventGameEdit}
        handleEventGameDelete={handleEventGameDelete}
      />

      <AlertSnackbar
        open={alert.open}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, open: false })}
      />
    </>
  );
};

export default ManageTeamPlayersContainer;
